/**
 * @file useBookingWizard.hook.ts
 * @path apps/website/src/features/booking/hooks/useBookingWizard.hook.ts
 * @description
 * Controls step navigation, dynamic step list, and per-step validation.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBookingStore } from "../booking.store";
import { validateStep, StepId } from "../booking.schema";
import { scrollToFirstError } from "@/utils/scrollToError";

// ---------------------------------------------------------------------------
// Step definitions
// ---------------------------------------------------------------------------

interface WizardStep {
  id: StepId;
  name: string;
}

const BASE_STEPS: WizardStep[] = [
  { id: "service", name: "Service" },
  { id: "activities", name: "Activities" },
  { id: "schedule-route", name: "Schedule" },
  { id: "trip-details", name: "Details" },
  { id: "review", name: "Review & Pay" },
];

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const useBookingWizard = () => {
  const selectedService = useBookingStore((s) => s.selectedService);
  const cameFromActivity = useBookingStore((s) => s.cameFromActivity);
  const state = useBookingStore();
  const triggerValidation = useBookingStore((s) => s.triggerValidation);
  const clearValidation = useBookingStore((s) => s.clearValidation);

  // Dynamic step list: remove Activities for airport transfers
  const steps = useMemo<WizardStep[]>(() => {
    if (selectedService?.slug === "airport-transfers") {
      return BASE_STEPS.filter((s) => s.id !== "activities");
    }
    return BASE_STEPS;
  }, [selectedService?.slug]);

  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-navigate when entering from an activity booking link
  const hasActivityNavigated = useRef(false);
  useEffect(() => {
    if (cameFromActivity && !hasActivityNavigated.current) {
      hasActivityNavigated.current = true;
      const idx = steps.findIndex((s) => s.id === "activities");
      if (idx !== -1) setCurrentIndex(idx);
    }
  }, [cameFromActivity, steps]);

  // Auto-navigate to activities step once a service is selected
  const hasAutoNavigated = useRef(false);
  useEffect(() => {
    if (selectedService && !hasAutoNavigated.current && !cameFromActivity) {
      hasAutoNavigated.current = true;
      const idx = steps.findIndex((s) => s.id === "activities");
      if (idx !== -1) setCurrentIndex(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  // Clamp index if steps length shrinks
  useEffect(() => {
    if (currentIndex >= steps.length) {
      setCurrentIndex(steps.length - 1);
    }
  }, [steps.length, currentIndex]);

  const activeStep = steps[currentIndex] ?? steps[0];
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === steps.length - 1;

  // ── Navigation ─────────────────────────────────────────────────────────────

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const goToStep = useCallback(
    (stepId: string) => {
      const idx = steps.findIndex((s) => s.id === stepId);
      if (idx !== -1) {
        clearValidation();
        setCurrentIndex(idx);
        scrollToTop();
      }
    },
    [steps, clearValidation],
  );

  const goNext = useCallback(() => {
    const result = validateStep(activeStep.id, state);

    if (!result.isValid) {
      triggerValidation();
      const ids = result.fieldErrors
        .map((e) => e.elementId)
        .filter((id): id is string => !!id);
      scrollToFirstError(ids);
      return;
    }

    clearValidation();
    if (!isLastStep) {
      setCurrentIndex((prev) => prev + 1);
      scrollToTop();
    }
  }, [activeStep.id, state, triggerValidation, clearValidation, isLastStep]);

  const goBack = useCallback(() => {
    if (!isFirstStep) {
      clearValidation();
      setCurrentIndex((prev) => prev - 1);
      scrollToTop();
    }
  }, [isFirstStep, clearValidation]);

  // ── Current step validation snapshot (for UI badges) ───────────────────────

  const currentValidation = useMemo(
    () => validateStep(activeStep.id, state),
    [activeStep.id, state],
  );

  return {
    steps,
    currentIndex,
    activeStep,
    isFirstStep,
    isLastStep,
    currentValidation,
    goNext,
    goBack,
    goToStep,
  };
};
